import React, { useEffect, useState, useMemo } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  "opened", "due_diligence", "funding_pending", "funds_received", "funding_confirmed",
  "transfer_pending", "buyer_review", "ready_for_settlement", "settlement_completed",
  "closed", "cancelled", "disputed",
];

const STATUS_TONE = {
  disputed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  cancelled: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  closed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  settlement_completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  ready_for_settlement: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
};

function StatusBadge({ status }) {
  const cls = STATUS_TONE[status] || "bg-muted text-muted-foreground";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-none ${cls}`}>
      {status?.replace(/_/g, " ") || "—"}
    </span>
  );
}

function Dot({ on, label, testid }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider whitespace-nowrap"
         data-testid={testid} title={label}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-500" : "bg-zinc-400"}`} />
      {label}
    </div>
  );
}

export default function DealsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [disputeOnly, setDisputeOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);          // deal id currently opened in drawer

  const load = () => {
    setLoading(true);
    const params = { limit: 300 };
    if (q) params.q = q;
    if (status) params.status = status;
    if (disputeOnly) params.dispute = "open";
    api.get("/admin/deals-detail", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status, disputeOnly]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [q]);

  return (
    <div className="space-y-4" data-testid="admin-deals-tab">
      <div className="flex flex-wrap items-center gap-2 border border-border p-3">
        <Input
          data-testid="deals-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search: deal id, buyer, seller, note id, email…"
          className="rounded-none max-w-md"
        />
        <Select value={status} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-none w-56" data-testid="deals-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          data-testid="deals-dispute-filter"
          variant={disputeOnly ? "default" : "outline"}
          className="rounded-none uppercase text-xs h-9"
          onClick={() => setDisputeOnly((v) => !v)}
        >
          {disputeOnly ? "Showing Open Disputes" : "Filter Disputes"}
        </Button>
        <Button variant="outline" className="rounded-none uppercase text-xs h-9 ml-auto" onClick={load} data-testid="deals-refresh">
          Refresh
        </Button>
      </div>

      <div className="border border-border overflow-x-auto" data-testid="deals-table">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-muted/50 text-left">
            <tr>
              <Th>Deal</Th><Th>Status</Th><Th>Buyer</Th><Th>Seller</Th><Th>Amount</Th>
              <Th>Funds</Th><Th>Docs</Th><Th>Buyer Accepted</Th><Th>Dispute</Th><Th>Settlement</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">No deals match filters.</td></tr>
            )}
            {items.map((d) => (
              <tr key={d.deal_id} className="border-t border-border align-top" data-testid={`deals-row-${d.deal_id}`}>
                <Td className="font-mono text-[11px]">
                  <Link to={`/deals/${d.deal_id}`} className="text-primary hover:underline" data-testid={`deals-open-${d.deal_id}`}>
                    {d.deal_id.slice(-10)}
                  </Link>
                  {d.note_id && <div className="text-[10px] text-muted-foreground">note {d.note_id.slice(-8)}</div>}
                </Td>
                <Td><StatusBadge status={d.status} /></Td>
                <Td className="text-[11px]">
                  <div>{d.buyer?.name || "—"}</div>
                  <div className="text-muted-foreground">{d.buyer?.email || d.buyer?.id?.slice(-8)}</div>
                </Td>
                <Td className="text-[11px]">
                  <div className="text-muted-foreground">{d.seller?.email || d.seller?.id?.slice(-8) || "—"}</div>
                </Td>
                <Td className="font-mono-data text-[12px]">{fmtMoney(d.final_amount)}</Td>
                <Td>
                  <div className="space-y-1">
                    <Dot on={d.funds_sent}     label="Sent"     testid={`deals-funds-sent-${d.deal_id}`} />
                    <Dot on={d.funds_received} label="Received" testid={`deals-funds-received-${d.deal_id}`} />
                    <Dot on={d.funds_cleared}  label="Cleared"  testid={`deals-funds-cleared-${d.deal_id}`} />
                  </div>
                </Td>
                <Td>
                  <div className="text-[11px] font-mono">{d.transfer_docs_count} file{d.transfer_docs_count === 1 ? "" : "s"}</div>
                  <Dot on={d.transfer_package_complete} label="Package" testid={`deals-pkg-${d.deal_id}`} />
                </Td>
                <Td>
                  <Dot on={d.buyer_accepted} label={d.buyer_accepted ? "Accepted" : "Pending"} testid={`deals-accepted-${d.deal_id}`} />
                </Td>
                <Td>
                  {d.dispute_status === "open" ? (
                    <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase rounded-none bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">Open</span>
                  ) : d.dispute_status === "resolved" ? (
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">Resolved</span>
                  ) : <span className="text-[10px] font-mono text-muted-foreground">—</span>}
                </Td>
                <Td>
                  {d.settlement_recorded ? (
                    <span className="text-[10px] font-mono uppercase text-emerald-600">Recorded</span>
                  ) : d.settlement_ready ? (
                    <span className="text-[10px] font-mono uppercase text-emerald-600">Ready</span>
                  ) : (
                    <span className="text-[10px] font-mono text-muted-foreground" title={d.settlement_gate_unmet?.join(", ")}>
                      Blocked
                    </span>
                  )}
                </Td>
                <Td>
                  <Button size="sm" variant="outline" className="rounded-none text-[10px] h-7 uppercase"
                          data-testid={`deals-manage-${d.deal_id}`}
                          onClick={() => setDetail(d.deal_id)}>
                    Manage
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && <DealDrawer dealId={detail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-muted-foreground text-[10px] whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;

function DealDrawer({ dealId, onClose }) {
  const [deal, setDeal] = useState(null);
  const [audit, setAudit] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");
  const [action, setAction] = useState(null); // "pause" | "resume" | "cancel"

  const load = () => {
    api.get(`/deals/${dealId}`).then((r) => setDeal(r.data)).catch(() => setDeal(null));
    api.get(`/admin/deals/${dealId}/audit`).then((r) => setAudit(r.data.items || [])).catch(() => setAudit([]));
  };
  useEffect(load, [dealId]);

  const saveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/deals/${dealId}/internal-note`, { text: note.trim() });
      toast.success("Internal note added");
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async () => {
    if (!action) return;
    if ((action === "pause" || action === "cancel") && !reason.trim()) {
      toast.error("Reason required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/deals/${dealId}/${action}`, { reason: reason.trim() || undefined });
      toast.success(`Deal ${action}d`);
      setAction(null); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="deals-drawer">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Deal {dealId.slice(-10)} <span className="text-sm text-muted-foreground">· {deal?.status}</span>
          </DialogTitle>
        </DialogHeader>

        {!deal ? <div className="text-muted-foreground p-4">Loading…</div> : (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Link to={`/deals/${dealId}`}>
                <Button className="rounded-none uppercase text-xs h-9" data-testid="drawer-open-deal-room">Open Deal Room</Button>
              </Link>
              {!deal.closed && deal.status !== "cancelled" && (
                <>
                  {deal.status !== "paused" ? (
                    <Button variant="outline" className="rounded-none uppercase text-xs h-9" onClick={() => setAction("pause")} data-testid="drawer-pause">
                      Pause Deal
                    </Button>
                  ) : (
                    <Button variant="outline" className="rounded-none uppercase text-xs h-9" onClick={() => setAction("resume")} data-testid="drawer-resume">
                      Resume Deal
                    </Button>
                  )}
                  <Button variant="outline" className="rounded-none uppercase text-xs h-9 text-red-600" onClick={() => setAction("cancel")} data-testid="drawer-cancel">
                    Cancel Deal
                  </Button>
                </>
              )}
            </div>

            <div className="border border-border p-4">
              <div className="font-overline text-accent mb-2">Add Internal Admin Note</div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="rounded-none" placeholder="Only visible to admins…" data-testid="drawer-note-input" />
              <div className="flex justify-end mt-2">
                <Button className="rounded-none uppercase text-xs h-9" onClick={saveNote} disabled={saving || !note.trim()} data-testid="drawer-note-save">Save Note</Button>
              </div>
              {(deal.internal_notes || []).length > 0 && (
                <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                  {(deal.internal_notes || []).map((n, i) => (
                    <div key={i} className="text-xs border-l-2 border-primary pl-3 py-1">
                      <div className="text-muted-foreground font-mono text-[10px]">{n.at ? new Date(n.at).toLocaleString() : ""}</div>
                      <div>{n.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-border p-4">
              <div className="font-overline text-accent mb-2">Full Audit Trail ({audit.length})</div>
              <div className="max-h-72 overflow-y-auto text-xs font-mono space-y-1" data-testid="drawer-audit">
                {audit.length === 0 ? <div className="text-muted-foreground">No events yet.</div> :
                  audit.map((e, i) => (
                    <div key={i} className="flex gap-3 border-b border-border/50 py-1">
                      <span className="text-muted-foreground whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
                      <span className="uppercase text-primary">{e.event}</span>
                      <span className="text-muted-foreground ml-auto">{e.actor_role || ""} {(e.actor_id || "").slice(-6)}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="drawer-close">Close</Button>
        </DialogFooter>
      </DialogContent>

      {action && (
        <Dialog open onOpenChange={() => { setAction(null); setReason(""); }}>
          <DialogContent className="rounded-none max-w-md" data-testid="drawer-action-dialog">
            <DialogHeader><DialogTitle className="capitalize">{action} deal</DialogTitle></DialogHeader>
            {(action === "pause" || action === "cancel") && (
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="rounded-none" data-testid="drawer-action-reason" />
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => { setAction(null); setReason(""); }}>Cancel</Button>
              <Button className="rounded-none" onClick={runAction} disabled={saving} data-testid="drawer-action-confirm">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
